/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState } from 'react';
import { TokenMetadata } from '../lib/pinata';
import { AiEngine } from '../lib/ai/engine';
import { TokenAnalytics } from '../types/ai';
import { Brain, Shield, TrendingUp, TrendingDown, Activity, AlertTriangle, Loader2, Clock, Store, Database, Zap, BarChart2, Share2 } from 'lucide-react';
import { formatCurrency, formatCompact } from '../lib/utils/format';
import { useMarketAnalytics } from '../hooks/use-market-analytics';

interface TokenInsightsProps {
    token: TokenMetadata;
    currentPrice: number;
}

export const TokenInsights: React.FC<TokenInsightsProps> = ({ token, currentPrice }) => {
    const [analytics, setAnalytics] = useState<TokenAnalytics | null>(null);
    const [aiLoading, setAiLoading] = useState(true);
    
    // Fetch aggregated market data
    const { marketData, isLoading: marketLoading } = useMarketAnalytics(token.properties?.mintAddress, token.symbol);

    useEffect(() => {
        let mounted = true;
        const analyze = async () => {
            setAiLoading(true);
            try {
                const data = await AiEngine.analyzeToken(token, currentPrice);
                if (mounted) setAnalytics(data);
            } catch (e) {
                console.error(e);
            } finally {
                if (mounted) setAiLoading(false);
            }
        };
        analyze();
        return () => { mounted = false; };
    }, [token, currentPrice]);

    if (aiLoading) {
        return (
            <div className="h-64 flex flex-col items-center justify-center text-stone-400 bg-stone-50 rounded-xl border border-stone-200">
                <Loader2 className="animate-spin mb-2" size={24} />
                <span className="text-xs font-mono animate-pulse">Running Neural Inference...</span>
            </div>
        );
    }

    if (!analytics) return null;

    const getRiskColor = (level: string) => {
        if (level === 'LOW') return 'text-green-600 bg-green-50 border-green-200';
        if (level === 'MEDIUM') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
        return 'text-red-600 bg-red-50 border-red-200';
    };

    // --- Market Data Calculations ---
    // Prefer aggregated real data if available, fallback to simulation/props
    
    const displayPrice = marketData?.price || currentPrice;
    const mcap = marketData?.price && marketData?.supply ? marketData.price * marketData.supply : (currentPrice * 1_000_000_000);
    const liquidity = marketData?.liquidity || (mcap * 0.15);
    const volume = marketData?.volume24h || 0;
    
    // 1. Launch Age
    const launchTime = token.properties?.createdAt || Date.now();
    const ageHours = Math.max(0, Math.floor((Date.now() - launchTime) / (1000 * 60 * 60)));
    const ageDisplay = ageHours < 1 ? '< 1h ago' : ageHours < 24 ? `${ageHours}h ago` : `${Math.floor(ageHours/24)}d ago`;

    // 2. DEX Status
    const isDexReady = !!token.properties?.marketId || !!token.properties?.txHash || (marketData && marketData.liquidity > 1000); 
    const dexName = token.properties?.chain === 'SUI' ? 'Cetus' : 'Raydium';
    const tradeStatus = isDexReady ? 'Trading Live' : 'Bonding Curve';

    // 4. Vicinity & Pressure Simulation
    const buyPressure = analytics.sentiment === 'BULLISH' ? 65 + Math.random() * 20 : 35 + Math.random() * 20;
    const sellPressure = 100 - buyPressure;

    return (
        <div className="space-y-4 animate-fade-in">
            {/* AI Summary Card */}
            <div className="p-4 bg-stone-900 text-stone-200 rounded-xl border border-stone-800 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-3 opacity-10">
                    <Brain size={64} />
                </div>
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                         <Brain size={16} className="text-nobel-gold" />
                         <span className="text-xs font-bold uppercase tracking-widest text-nobel-gold">AI Insight</span>
                    </div>
                    <p className="text-sm leading-relaxed font-light italic">"{analytics.summary}"</p>
                </div>
            </div>

            {/* Score & Risk Grid */}
            <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white border border-stone-200 rounded-xl flex flex-col items-center justify-center shadow-sm">
                    <span className="text-[10px] font-bold text-stone-400 uppercase mb-1">Confidence Score</span>
                    <div className="text-2xl font-serif font-bold text-stone-900">{analytics.score}<span className="text-sm text-stone-400">/100</span></div>
                </div>
                <div className={`p-3 border rounded-xl flex flex-col items-center justify-center shadow-sm ${getRiskColor(analytics.riskLevel)}`}>
                    <span className="text-[10px] font-bold opacity-70 uppercase mb-1">Risk Profile</span>
                    <div className="text-lg font-bold flex items-center gap-1">
                        <Shield size={16} /> {analytics.riskLevel}
                    </div>
                </div>
            </div>

            {/* Market Status Analytics (Detailed) */}
            <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl relative">
                <div className="flex items-center justify-between mb-4 pb-2 border-b border-stone-200/60">
                    <div className="flex items-center gap-2">
                        <Activity size={14} className="text-stone-500"/>
                        <span className="text-xs font-bold text-stone-500 uppercase tracking-wide">Market Analytics</span>
                    </div>
                    
                    {marketData && (
                        <div className="flex -space-x-1">
                            {marketData.sources.slice(0, 3).map((source, i) => (
                                <div key={i} className="w-4 h-4 rounded-full bg-white border border-stone-200 flex items-center justify-center text-[8px] font-bold text-stone-500" title={`Verified by ${source}`}>
                                    {source[0]}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="space-y-1">
                        <div className="text-[10px] text-stone-400 uppercase font-bold">Current Price</div>
                        <div className="font-mono font-bold text-stone-900 text-lg">{formatCurrency(displayPrice, 'USD', 6)}</div>
                    </div>
                    <div className="space-y-1 text-right">
                        <div className="text-[10px] text-stone-400 uppercase font-bold">Market Cap</div>
                        <div className="font-mono font-bold text-stone-900 text-lg">${formatCompact(mcap)}</div>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-stone-200/60">
                    <div className="bg-white p-2 rounded-lg border border-stone-100">
                        <div className="text-[9px] text-stone-400 uppercase mb-1 flex items-center justify-center gap-1"><Clock size={10}/> Age</div>
                        <div className="font-mono text-xs font-bold text-stone-700">{ageDisplay}</div>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-stone-100">
                        <div className="text-[9px] text-stone-400 uppercase mb-1 flex items-center justify-center gap-1"><BarChart2 size={10}/> 24h Vol</div>
                        <div className="font-mono text-xs font-bold text-stone-700">${formatCompact(volume)}</div>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-stone-100">
                        <div className="text-[9px] text-stone-400 uppercase mb-1 flex items-center justify-center gap-1"><Zap size={10}/> Liq</div>
                        <div className="font-mono text-xs font-bold text-stone-700">${formatCompact(liquidity)}</div>
                    </div>
                </div>
            </div>

            {/* NEW: Liquidity Vicinity & Pressure Section */}
            <div className="p-4 bg-white border border-stone-200 rounded-xl shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-stone-500 uppercase tracking-wide flex items-center gap-2">
                        <BarChart2 size={12} /> Liquidity Vicinity
                    </span>
                    <span className="text-[10px] font-mono text-stone-400 bg-stone-50 px-2 py-0.5 rounded">Range: ±5%</span>
                </div>

                {/* Vicinity Gradient Bar */}
                <div className="relative h-14 w-full bg-stone-50 rounded-lg border border-stone-100 overflow-hidden flex items-center px-4 shadow-inner">
                    {/* Background Depth Gradients */}
                    <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-green-50/10 via-green-100/40 to-green-200/60"></div>
                    <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-red-50/10 via-red-100/40 to-red-200/60"></div>
                    
                    {/* Support/Resistance Lines */}
                    <div className="absolute top-3 bottom-3 left-[20%] w-0.5 bg-green-400/30 border-l border-dashed border-green-500/50"></div>
                    <div className="absolute top-3 bottom-3 right-[20%] w-0.5 bg-red-400/30 border-l border-dashed border-red-500/50"></div>

                    {/* Price Marker */}
                    <div className="absolute left-1/2 -translate-x-1/2 h-full w-0.5 bg-stone-800 z-10 flex flex-col items-center justify-center">
                        <div className="w-16 bg-stone-900 text-white text-[9px] font-mono py-0.5 rounded text-center shadow-lg mb-8">
                            {formatCurrency(displayPrice)}
                        </div>
                        <div className="w-1.5 h-1.5 bg-stone-900 rotate-45 -mt-1"></div>
                    </div>
                    
                    {/* Labels */}
                    <span className="absolute left-3 bottom-2 text-[9px] font-bold text-green-600 uppercase tracking-tight">Support</span>
                    <span className="absolute right-3 bottom-2 text-[9px] font-bold text-red-600 uppercase tracking-tight">Resistance</span>
                </div>

                {/* Pressure Bar Chart */}
                <div>
                    <div className="flex justify-between text-[10px] font-bold text-stone-500 mb-1.5 uppercase tracking-wide">
                        <span className="text-green-600">Buy Pressure {buyPressure.toFixed(0)}%</span>
                        <span className="text-red-600">Sell Pressure {sellPressure.toFixed(0)}%</span>
                    </div>
                    <div className="flex w-full h-2 rounded-full overflow-hidden bg-stone-100 border border-stone-100">
                        <div className="h-full bg-green-500 transition-all duration-1000" style={{ width: `${buyPressure}%` }}></div>
                        <div className="h-full bg-red-500 transition-all duration-1000" style={{ width: `${sellPressure}%` }}></div>
                    </div>
                </div>
            </div>

            {/* Prediction Stats */}
            <div className="p-4 bg-white border border-stone-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold text-stone-500 uppercase">24h Forecast</span>
                    <div className={`flex items-center gap-1 text-xs font-bold ${analytics.predictedTrend === 'UP' ? 'text-green-600' : 'text-red-500'}`}>
                        {analytics.predictedTrend === 'UP' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        {analytics.predictedTrend}
                    </div>
                </div>
                
                {/* Mini Graph (CSS/SVG only) */}
                <div className="h-20 w-full flex items-end gap-1 mb-2 border-b border-stone-100 pb-1">
                    {analytics.predictionGraph.map((point, i) => (
                        <div 
                            key={i} 
                            className={`flex-1 rounded-t-sm transition-all hover:opacity-80 ${analytics.predictedTrend === 'UP' ? 'bg-green-200' : 'bg-red-200'}`}
                            style={{ 
                                height: `${((point.price - (currentPrice * 0.8)) / (currentPrice * 0.4)) * 100}%`,
                                minHeight: '10%'
                            }}
                            title={`Forecast: ${formatCurrency(point.price)}`}
                        ></div>
                    ))}
                </div>
                <div className="flex justify-between text-[10px] text-stone-400 font-mono">
                    <span>Now</span>
                    <span className="font-bold text-stone-600">Target: {formatCurrency(analytics.predictedPrice24h)}</span>
                </div>
            </div>

            {/* Audit Flags */}
            {analytics.auditFlags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {analytics.auditFlags.map((flag, i) => (
                        <span key={i} className="px-2 py-1 bg-stone-100 text-stone-600 text-[10px] font-bold uppercase rounded border border-stone-200 flex items-center gap-1">
                            {flag === 'Audited' ? <Shield size={10} className="text-green-500" /> : <AlertTriangle size={10} className="text-amber-500" />}
                            {flag}
                        </span>
                    ))}
                    <span className="px-2 py-1 bg-stone-100 text-stone-600 text-[10px] font-bold uppercase rounded border border-stone-200 flex items-center gap-1">
                        <Activity size={10} className="text-blue-500" /> Whales: {analytics.whaleActivity}
                    </span>
                </div>
            )}
        </div>
    );
};